import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AiOutlineSync } from "react-icons/ai";

import DatePicker from "react-datepicker";
import DataGrid, {
  Column,
  HeaderRendererProps,
  SortColumn,
  useFocusRef,
} from "react-data-grid";
import { API, graphqlOperation } from "aws-amplify";
import {
  ltpPriceByMaterialCode,
  ltpPriceByLastSyncDate,
} from "../../graphql/queries";

import { format } from "date-fns";
import EmptyRowsRenderer from "../../components/EmptyRow";
import FilterRowsRenderer from "../../components/Filter";
import ManualSync from "../../components/ManualSync";
import LoadingRowsRenderer from "../../components/Loading";
import { syncLtpPrice } from "../../graphql/mutations";
import { ProductLibLtpPrice } from "../../API";
import {
  getComparator,
  dataGridTabClassname,
  inputStopPropagation,
  filterClassname,
  styleDataGrid,
  filterColumnClassName,
  filterCellClass,
} from "../../datagrid/extra";
import ModalConfirm from "../../components/ModalConfirm";
import Icon from "../../components/Icon";
import { Link } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { FaSearch, FaSpinner } from "react-icons/fa";
import Select from "react-select";

interface Filter extends Omit<ProductLibLtpPrice, "id"> {
  id: string;
  enabled: boolean;
}

const FilterContext = createContext<Filter | undefined>(undefined);

interface syncInterface {
  syncDate?: string;
  materialCode?: string;
}

interface SummaryRow {
  id: string;
  totalCount: number;
}

function ControlPanalSyncLtpPrice() {
  const [rows, setRows] = useState<any>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showConfirmSync, setShowConfirmSync] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([
    { columnKey: "updatedAt", direction: "DESC" },
  ]);

  const [syncType, setSyncType] = useState("");
  const [searchType, setSearchType] = useState("searchDate");
  const [searchMaterialCode, setSearchMaterialCode] = useState("");
  const [searchDate, setSearchDate] = useState(new Date());
  const [isDisabledSearch, setIsDisabledSearch] = useState(false);

  const [syncDate, setSyncDate] = useState(new Date());
  const [syncMaterialCode, setSyncMaterialCode] = useState("");
  const [disableSync, setDisableSync] = useState(false);

  const searchTypeOptions = [
    {
      label: "By SyncDate",
      value: "searchDate",
    },
    {
      label: "By MatCode",
      value: "searchMaterialCode",
    },
  ];

  const syncTypeOptions = [
    {
      label: "By Date",
      value: "syncDate",
    },
    {
      label: "By MatCode",
      value: "syncMaterialCode",
    },
  ];

  const [filters, setFilters] = useState<Filter>({
    __typename: "ProductLibLtpPrice",
    id: "",
    ltpId: "",
    materialCode: "",
    pricePerDozen: 0,
    periodStart: "",
    periodEnd: "",
    lastSyncDate: "",
    createdAt: "",
    updatedAt: "",
    enabled: false,
  });

  const dateFormat = "dd-MM-yyyy (HH:mm:ss)";
  const filteredRows = useMemo(() => {
    return rows.filter((r: any) => {
      return (
        (filters.id
          ? r.id
            ? r.id.toLocaleLowerCase().includes(filters.id.toLowerCase())
            : false
          : true) &&
        (filters.ltpId
          ? r.ltpId
            ? r.ltpId.toLocaleLowerCase().includes(filters.ltpId.toLowerCase())
            : false
          : true) &&
        (filters.materialCode
          ? r.materialCode
            ? r.materialCode
                .toLocaleLowerCase()
                .includes(filters.materialCode.toLowerCase())
            : false
          : true) &&
        (filters.pricePerDozen
          ? r.pricePerDozen
            ? r.pricePerDozen
                .toLocaleLowerCase()
                .includes(filters.pricePerDozen)
            : false
          : true) &&
        (filters.periodStart
          ? r.periodStart
            ? r.periodStart
                .toLocaleLowerCase()
                .includes(filters.periodStart.toLowerCase())
            : false
          : true) &&
        (filters.periodEnd
          ? r.periodEnd
            ? r.periodEnd
                .toLocaleLowerCase()
                .includes(filters.periodEnd.toLowerCase())
            : false
          : true) &&
        (filters.lastSyncDate
          ? r.lastSyncDate
            ? r.lastSyncDate
                .toLocaleLowerCase()
                .includes(filters.lastSyncDate.toLowerCase())
            : false
          : true) &&
        (filters.createdAt
          ? r.createdAt
            ? format(new Date(r.createdAt), dateFormat)
                .toLocaleLowerCase()
                .includes(filters.createdAt.toLowerCase())
            : false
          : true) &&
        (filters.updatedAt
          ? r.updatedAt
            ? format(new Date(r.updatedAt), dateFormat)
                .toLocaleLowerCase()
                .includes(filters.updatedAt.toLowerCase())
            : false
          : true)
      );
    });
  }, [rows, filters]);

  function clearFilters() {
    setFilters({
      __typename: "ProductLibLtpPrice",
      id: "",
      ltpId: "",
      materialCode: "",
      pricePerDozen: 0,
      periodStart: "",
      periodEnd: "",
      lastSyncDate: "",
      createdAt: "",
      updatedAt: "",
      enabled: false,
    });
  }

  function toggleFilters() {
    setFilters((filters) => ({
      ...filters,
      enabled: !filters.enabled,
    }));
  }

  const sortedRows = useMemo((): readonly ProductLibLtpPrice[] => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === "ASC" ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  const summaryRows = useMemo(() => {
    const summaryRow: SummaryRow = {
      id: "total_0",
      totalCount: sortedRows.length,
    };
    return [summaryRow];
  }, [sortedRows]);

  const fetchLtpPriceByMaterialCode = async (materialCode: string) => {
    if (materialCode) {
      let result: any = await API.graphql(
        graphqlOperation(ltpPriceByMaterialCode, {
          materialCode: materialCode,
          limit: 1000,
        })
      );

      console.log("result fetchLtpPriceByMaterialCode : ", result);
      if (result) setRows(result.data.ltpPriceByMaterialCode.items);
      else setRows([]);
    } else {
      setRows([]);
    }
  };

  const fetchLtpPriceByLastSyncDate = async (lastSyncDate: string) => {
    if (lastSyncDate) {
      let result: any = await API.graphql(
        graphqlOperation(ltpPriceByLastSyncDate, {
          lastSyncDate: lastSyncDate,
          limit: 1000,
        })
      );

      console.log("result fetchLtpPriceByLastSyncDate : ", result);
      if (result) setRows(result.data.ltpPriceByLastSyncDate.items);
      else setRows([]);
    } else {
      setRows([]);
    }
  };

  const searchLtpPrice = async () => {
    setIsDisabledSearch(true);
    if (searchType === "searchDate") {
      await fetchLtpPriceByLastSyncDate(
        searchDate ? format(searchDate, "yyyy-MM-dd") : ""
      );
    }
    if (searchType === "searchMaterialCode") {
      await fetchLtpPriceByMaterialCode(searchMaterialCode);
    }
    setIsDisabledSearch(false);
  };

  useEffect(() => {
    fetchLtpPriceByLastSyncDate(
      searchDate ? format(searchDate, "yyyy-MM-dd") : ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function triggerSyncFn() {
    setIsLoading(true);
    // setRows([]);
    let syncParam: syncInterface = {};
    if (syncType === "syncDate") {
      if (!syncDate) {
        setIsLoading(false);
        setShowConfirmSync(false);
        console.log("syncDate is empty");
        return false;
      }
      syncParam = {
        syncDate: format(syncDate, "yyyyMMdd"),
        materialCode: "",
      };
    }
    if (syncType === "syncMaterialCode") {
      if (!syncMaterialCode) {
        setIsLoading(false);
        setShowConfirmSync(false);
        console.log("syncMaterialCode is empty");
        return false;
      }
      syncParam = {
        syncDate: "",
        materialCode: syncMaterialCode,
      };
    }

    console.log("syncParam : ", syncParam);

    // setShowConfirmSync(false)
    // setShowDialog(true)

    let response = await API.graphql(graphqlOperation(syncLtpPrice, syncParam));

    if (response) {
      // if (searchType === "searchDate") {
      //   await fetchLtpPriceByLastSyncDate(
      //     searchDate ? format(searchDate, "yyyy-MM-dd") : ""
      //   );
      // }
      // if (searchType === "searchMaterialCode") {
      //   await fetchLtpPriceByMaterialCode(searchMaterialCode);
      // }
      setIsLoading(false);
      setShowConfirmSync(false);
      setShowDialog(true);
      console.log("res : ", response);
    } else {
      console.log("error : ", response);
    }
  }

  const syncLtpPriceColumns: Column<ProductLibLtpPrice, SummaryRow>[] = [
    {
      key: "id",
      name: "ID",
      minWidth: 100,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center" title={`${row.id}`}>
            {row.id.substring(0, 7)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.id ? filters.id : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  id: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
      summaryFormatter({ row }) {
        return (
          <div className="opacity-50">
            <strong>({row.totalCount} Rows)</strong>
          </div>
        );
      },
    },
    {
      key: "ltpId",
      name: "LTP ID",
      minWidth: 100,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center" title={`${row.ltpId}`}>
            {row.ltpId.substring(0, 7)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.ltpId ? filters.ltpId : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  ltpId: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "materialCode",
      name: "Mat Code",
      minWidth: 100,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => (
        <div className="text-center" title={row.materialCode}>
          {row.materialCode}
        </div>
      ),
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.materialCode ? filters.materialCode : ""}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  materialCode: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "pricePerDozen",
      name: "Price Per Dozen",
      minWidth: 150,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center text-right">
            {row.pricePerDozen.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.pricePerDozen ? filters.pricePerDozen : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  pricePerDozen: e.target.value
                    ? parseFloat(e.target.value)
                    : 0,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "periodStart",
      name: "Period Start",
      minWidth: 170,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.periodStart), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.periodStart ? filters.periodStart : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  periodStart: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "periodEnd",
      name: "Period End",
      minWidth: 170,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.periodEnd), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => (
            <input
              {...rest}
              className={filterClassname}
              value={filters.periodEnd ? filters.periodEnd : ""}
              onChange={(e) => {
                console.log("e : ", e);
                setFilters({
                  ...filters,
                  periodEnd: e.target.value,
                });
              }}
              onKeyDown={inputStopPropagation}
            />
          )}
        </FilterRenderer>
      ),
    },
    {
      key: "createdAt",
      name: "Created At",
      minWidth: 170,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.createdAt), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => <></>}
        </FilterRenderer>
      ),
    },
    {
      key: "updatedAt",
      name: "Updated At",
      minWidth: 170,
      resizable: true,
      sortable: true,
      headerCellClass: filterColumnClassName,
      cellClass: filterCellClass,
      formatter: ({ row }) => {
        return (
          <div className="items-center">
            {format(new Date(row.updatedAt), dateFormat)}
          </div>
        );
      },
      headerRenderer: (p) => (
        <FilterRenderer<ProductLibLtpPrice, SummaryRow, HTMLInputElement>
          {...p}
        >
          {({ filters, ...rest }) => <></>}
        </FilterRenderer>
      ),
    },
  ];

  return (
    <>
      <ModalConfirm
        show={showDialog}
        onHide={() => {}}
        title="Result sync SAP"
        cancelText={"Close"}
        cancelAction={() => {
          setShowDialog(false);
          setShowConfirmSync(false);
          setIsLoading(false);
        }}
        okClass="btn-submit"
        maxWidth="max-w-4xl"
      >
        <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
          <strong>Sent sync sap actions done</strong>
          <p>Please wait 1-2 mins then refresh screen for new data ...</p>
        </div>
      </ModalConfirm>

      <ModalConfirm
        show={showConfirmSync}
        onHide={() => {}}
        title="Sync sap Confirmation !!"
        cancelText={"Close"}
        cancelAction={() => setShowConfirmSync(false)}
        okText={"Confirm"}
        loading={isLoading}
        okAction={() => {
          setDisableSync(false);
          triggerSyncFn();
          setIsLoading(true);
        }}
        okClass="btn-submit"
        maxWidth="max-w-md"
      >
        <>
          <div className="flex flex-col items-center justify-center mb-2 space-y-2 text-center">
            <AiOutlineSync size={36} color="rgb(167 139 250)" />
            <h6>Do you want to sync ltp price master from sap</h6>
            {syncType === "syncDate" && (
              <h6>{`Sync Date : ${format(syncDate, "yyyy-MM-dd")}`}</h6>
            )}
            {syncType === "syncMaterialCode" && (
              <h6>{`Material Code : ${
                syncMaterialCode ? syncMaterialCode : "-"
              }`}</h6>
            )}
          </div>
        </>
      </ModalConfirm>

      <div>
        <div className="page-container-title">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 w-full">
            <div className="grid grid-cols-5 gap-0.5 md:gap-1 items-center lg:page-container-only-button">
              <div className="sm:flex sm:flex-row col-span-4">
                <div className="">
                  <label className="font-bold">Search</label>
                  <Select
                    className="font-normal text-sm w-[9rem]"
                    classNamePrefix="select"
                    placeholder="Search Type"
                    isSearchable={true}
                    defaultValue={searchTypeOptions[0]}
                    isClearable={false}
                    isDisabled={false}
                    options={searchTypeOptions}
                    onChange={(vals) => setSearchType(vals?.value!)}
                  />
                </div>
                {searchType === searchTypeOptions[0].value && (
                  <div className="w-28 mt-1 sm:mt-6">
                    <DatePicker
                      selected={searchDate}
                      onChange={(date: any) => {
                        if (date) {
                          setSearchDate(date);
                        }
                      }}
                      dateFormat={"yyyy-MM-dd"}
                      selectsStart
                      startDate={searchDate}
                      maxDate={new Date()}
                      className="form-input-control-panel-2"
                    />
                  </div>
                )}
                {searchType === searchTypeOptions[1].value && (
                  <div className="w-28 mt-1 sm:mt-6">
                    <input
                      className="form-input-control-panel-2"
                      type="text"
                      placeholder="MaterialCode"
                      onChange={(e) => {
                        setSearchMaterialCode(e.target.value);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-row items-center justify-end mt-6">
                <button
                  type="button"
                  className="flex btn-manual-sync"
                  onClick={() => {
                    searchLtpPrice();
                  }}
                  disabled={isDisabledSearch}
                >
                  {isDisabledSearch ? (
                    <FaSpinner className={`mt-0.5 mr-3 animate-spin`} />
                  ) : (
                    <FaSearch className={`mt-0.5 mr-3`} />
                  )}
                  Search
                </button>
              </div>
            </div>

            <div className="grid grid-cols-5 gap-0.5 md:gap-1 items-center lg:page-container-right justify-center">
              <div className="sm:flex sm:flex-row col-span-4">
                <div className="">
                  <label className="font-bold">Sync</label>
                  <Select
                    className="font-normal text-sm w-[9rem]"
                    classNamePrefix="select"
                    placeholder="By ..."
                    isSearchable={true}
                    isClearable={false}
                    isDisabled={false}
                    options={syncTypeOptions}
                    onChange={(vals) => {
                      setSyncType(vals?.value!);
                      setDisableSync(true);
                    }}
                  />
                </div>
                {syncType === syncTypeOptions[0].value && (
                  <div className="w-28 mt-1 sm:mt-6">
                    <DatePicker
                      selected={syncDate}
                      onChange={(date: any) => {
                        if (date) {
                          setSyncDate(date);
                          setDisableSync(true);
                        }
                      }}
                      dateFormat={"yyyy-MM-dd"}
                      selectsStart
                      startDate={syncDate}
                      maxDate={new Date()}
                      className="form-input-control-panel-2"
                    />
                  </div>
                )}
                {syncType === syncTypeOptions[1].value && (
                  <div className="w-28 mt-1 sm:mt-6">
                    <input
                      className="form-input-control-panel-2"
                      type="text"
                      placeholder="MaterialCode"
                      onChange={(e) => {
                        setSyncMaterialCode(e.target.value);
                        setDisableSync(true);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-row items-center justify-end mt-6">
                <ManualSync
                  setShowConfirmSync={setShowConfirmSync}
                  isDisabled={!disableSync}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between mb-3 mt-5">
          <FilterRowsRenderer
            toggleFilters={toggleFilters}
            clearFilters={clearFilters}
            activeFilters={filters}
          />
        </div>

        <FilterContext.Provider value={filters}>
          <DataGrid
            columns={syncLtpPriceColumns}
            rows={sortedRows}
            bottomSummaryRows={summaryRows}
            renderers={{ noRowsFallback: <EmptyRowsRenderer /> }}
            style={{ height: "60vh" }}
            headerRowHeight={filters.enabled ? 100 : 50}
            sortColumns={sortColumns}
            onSortColumnsChange={setSortColumns}
            className={dataGridTabClassname}
          />
          <div className="loading-renderer">
            {isLoading && <LoadingRowsRenderer />}
          </div>
        </FilterContext.Provider>
        <style>{styleDataGrid}</style>
      </div>
    </>
  );
}

function FilterRenderer<R, SR, T extends HTMLOrSVGElement>({
  isCellSelected,
  onSort,
  sortDirection,
  column,
  children,
}: HeaderRendererProps<R, SR> & {
  children: (args: {
    ref: React.RefObject<T>;
    tabIndex: number;
    filters: Filter;
  }) => React.ReactElement;
}) {
  const filters = useContext(FilterContext)!;
  const { ref, tabIndex } = useFocusRef<T>(isCellSelected);
  return (
    <>
      <div
        className="flex flex-row"
        onClick={() => {
          onSort(false);
        }}
      >
        <div className="flex flex-row mt-1 ml-2 mr-auto text-truncate">
          <span>{column.name}</span>
        </div>
        <div className="mt-5">
          {sortDirection === "ASC" && <Icon name="sort-alpha-down" />}
          {sortDirection === "DESC" && <Icon name="sort-alpha-up" />}
          {sortDirection !== "DESC" && sortDirection !== "ASC" && (
            <Icon name="arrows-alt-v" />
          )}
        </div>
      </div>
      {filters.enabled && (
        <div className="">{children({ ref, tabIndex, filters })}</div>
      )}
    </>
  );
}

export default ControlPanalSyncLtpPrice;
